import { getCurrentUser } from "@src/auth/getCurrentUser";
import { queryClient } from "@src/index";
import { collection, getFirestore, query, where } from "firebase/firestore";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { loadParsedFirebaseCollectionWhere, loadParsedFirebaseDocument } from "../generic/firebaseLoaders";
import { useRealtimeCollectionData } from "../generic/useRealtimeCollectionData";
import { fetchUser } from "../user/user.service";
import { IWorkflow, TWorkflow } from "./workflows.types";

const fetchWorkflow = async (id: string | undefined) => {
  const document = await loadParsedFirebaseDocument(`workflows/${id}`, TWorkflow);
  return document.valid ? document.data : null;
};

export const useWorkflowByIDKey = (id: string | undefined) => ["workflows", { id }];
export const useWorkflowByID = (id: string | undefined) => {
  const $workflow = useQuery(useWorkflowByIDKey(id), () => fetchWorkflow(id), {
    enabled: !!id,
  });

  return {
    $workflow,
    workflow: $workflow.data,
  };
};

export const useRealtimeWorkflowsInCompany = (companyID: string) => {
  const queryMemo = useMemo(
    () => query(collection(getFirestore(), "workflows"), where("readAccess.companies", "array-contains", companyID)),
    [companyID]
  );
  const { data, loading } = useRealtimeCollectionData(queryMemo, TWorkflow);

  return {
    workflows: data,
    isLoading: loading,
  };
};

const fetchWorkflowsForCompany = (companyID: string) => {
  return loadParsedFirebaseCollectionWhere(`workflows`, TWorkflow, where("readAccess.companies", "array-contains", companyID));
};

const fetchAllWorkflows = async (userID: string): Promise<IWorkflow[]> => {
  console.log("fetchAllWorkflows", userID);
  const user = await fetchUser(userID);
  const companies = user?.companies;
  if (!companies?.length) return [];

  const result: IWorkflow[] = [];
  await Promise.allSettled(
    companies.map(async (company) => {
      const workflows = await fetchWorkflowsForCompany(company);
      result.push(...workflows.docs.map((doc) => doc.data));
    })
  );
  console.log("fetchAllWorkflows", result);
  return result;
};

export const useWorkflowsKey = () => ["workflows"];
export const useWorkflows = () => {
  const { userId } = getCurrentUser();
  const $workflows = useQuery(useWorkflowsKey(), () => fetchAllWorkflows(userId));
  return {
    $workflows,
    workflows: $workflows.data,
  };
};
export const prefetchWorkflows = async (userID: string) => {
  console.log("prefetchWorkflows 1", userID);
  const workflows = await fetchAllWorkflows(userID);
  console.log("prefetchWorkflows 2", workflows);
  queryClient.setQueryData(useWorkflowsKey(), workflows);
  console.log("prefetchWorkflows 3", queryClient.getQueryData(useWorkflowsKey()));
  return workflows;
};

export const getWorkflowsFromCache = () => {
  const key = useWorkflowsKey();
  console.log("Cache key being used:", key);
  const cache = queryClient.getQueryData<IWorkflow[]>(key);
  console.log("getWorkflowsFromCache", cache);
  if (!cache) return [];
  return cache;
};
